/* eslint-disable react/jsx-props-no-spreading */
import { Category, Service, isPrivacyButtonUI, isTcfUI } from '@usercentrics/cmp-browser-sdk';
import { ComponentChild, FunctionalComponent, h } from 'preact';
import { Ref, useEffect } from 'preact/hooks';
import { forwardRef, memo } from 'preact/compat';
import MapCategories from '../CategoryHandler/MapCategories';
import { VIEW } from '../../enums';
import VirtualCategoryItem from '../VirtualList/VirtualCategoryItem';
import { VirtualChildContainer } from '../VirtualList/VirtualList.style';
import VirtualList from '../VirtualList';
import { getItemStyle } from '../VirtualList/helpers';
import { useCategoryContext } from '../../contexts/CategoryContext';
import { useSettingsContext } from '../../contexts/SettingsContext';
import { useTheme } from 'styled-components';
import { useUiContext } from '../../contexts/UiContext';
import { useVirtualList } from '../../hooks';

const LIST_TOP_SPACE = 1;

export type CategoryProps = {
  category: Category;
  checked: boolean;
  onCategoryToggle: () => void;
  onServiceToggle: (service: Service) => void;
};

type Props = {
  isVisible: boolean;
  showServicesToggles: boolean;
  stickyRef: Ref<HTMLDivElement>;
};

const CategoriesList: FunctionalComponent<Props> = forwardRef<HTMLDivElement, Props>(
  ({ isVisible, showServicesToggles = true, stickyRef }, virtualListRef) => {
    const categories = MapCategories(useCategoryContext());
    const itemCount = categories.length + LIST_TOP_SPACE;
    const { getItemSize, setItemSize } = useVirtualList(itemCount, 50);
    const { clearCategoryPurposeScrollToId, scrollToIdTabViewCategoryPurpose, previousView } = useUiContext();
    const { ui } = useSettingsContext();
    const theme = useTheme();

    // Clears the scroll to index to avoid jumping back on it after the component rendered
    useEffect(() => {
      if (isVisible) {
        clearCategoryPurposeScrollToId();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    if (!ui || isPrivacyButtonUI(ui)) {
      return null;
    }

    const getScrollIndexForSlug = (): number => {
      if (scrollToIdTabViewCategoryPurpose && typeof scrollToIdTabViewCategoryPurpose === 'string') {
        const index = categories.findIndex(
          (categoryProp) => categoryProp.category.slug === scrollToIdTabViewCategoryPurpose,
        );

        // index found --> return correct whole list index by adding static Placeholder in the top of the list
        if (index >= 0) {
          return index + LIST_TOP_SPACE;
        }
      }

      return -1;
    };

    // Do not keep previous scroll - If previousView was First Layer we reset the list scroll position.
    const addScroll = previousView !== VIEW.FIRST_LAYER && scrollToIdTabViewCategoryPurpose;

    return (
      <VirtualList
        data-testid="uc-virtual-list"
        ref={virtualListRef}
        estimatedItemSize={50}
        marginBottom={theme.spacing.base.lg}
        isVisible={isVisible}
        itemCount={itemCount}
        itemSize={getItemSize}
        stickyRef={stickyRef}
        {...(addScroll && { scrollToIndex: getScrollIndexForSlug() })}
        renderItem={({ index, style }): ComponentChild => (
          <VirtualChildContainer key={index} style={getItemStyle(style)}>
            <VirtualCategoryItem
              data-testid={`uc-virtual-list-item-${index}`}
              categories={categories}
              getItemSize={getItemSize}
              hasTopSpace
              index={index}
              id={index}
              {...(addScroll && { scrollToId: scrollToIdTabViewCategoryPurpose })}
              setItemSize={setItemSize}
              showToggles={isTcfUI(ui) ? !ui.secondLayer.hideToggles : true}
              showServicesToggles={showServicesToggles}
            />
          </VirtualChildContainer>
        )}
        width="100%"
      />
    );
  },
);

export default memo(CategoriesList);
